/** One scan of the cave (docs/spec/02 §1): every cell in reading order, then the clocks. */

import { BIRTH_STAGES, E } from './elements';
import { amoeba, amoebaEndOfScan } from './rules/amoeba';
import { butterfly, firefly } from './rules/creatures';
import { doorOpening, expandingWall } from './rules/deep';
import { digby } from './rules/digby';
import { advanceStage, inbox } from './rules/explode';
import { falling, standing } from './rules/objects';
import { RULES } from './tuning';
import type { CaveState, Input } from './types';

function act(s: CaveState, x: number, y: number, el: number, input: Input): void {
  switch (el) {
    case E.BOULDER: standing(s, x, y, true); return;
    case E.DIAMOND: standing(s, x, y, false); return;
    case E.BOULDER_FALLING: falling(s, x, y, true); return;
    case E.DIAMOND_FALLING: falling(s, x, y, false); return;
    case E.DIGBY: digby(s, x, y, input); return;
    case E.AMOEBA: amoeba(s, x, y); return;
    case E.INBOX: inbox(s, x, y); return;
    case E.EXPANDING_WALL: expandingWall(s, x, y); return;
    case E.DOOR_OPENING: doorOpening(s, x, y); return;
    default:
      if (el >= E.FIREFLY && el < E.FIREFLY + 4) firefly(s, x, y, el);
      else if (el >= E.BUTTERFLY && el < E.BUTTERFLY + 4) butterfly(s, x, y, el);
      else if (el >= E.EXPLODE && el < E.BIRTH + BIRTH_STAGES) advanceStage(s, x, y, el);
  }
}

/** Cave time moves on by one tick: the magic wall's timer and, once Digby is born, the clock. */
function clocks(s: CaveState): void {
  const tick = s.params.tickMs;
  s.ms += tick;
  if (s.magic.phase === 'active') {
    s.magic.msLeft -= tick;
    if (s.magic.msLeft <= 0) s.magic.phase = 'expired';
  }
  if (s.bornMs < 0 || s.status !== 'playing') return;
  s.secondMs += tick;
  while (s.secondMs >= 1000 && s.status === 'playing') {
    s.secondMs -= 1000;
    s.timeLeft = Math.max(0, s.timeLeft - 1);
    if (s.timeLeft === 0) {
      s.status = 'timeout';
      s.events.push({ type: 'timeout' });
    } else if (s.timeLeft <= RULES.warnSeconds) {
      s.events.push({ type: 'tick', seconds: s.timeLeft });
    }
  }
}

/**
 * Run one scan with this input. A won or timed-out cave is frozen; after a death the cave runs on
 * (explosions play out, boulders settle). Events for the scan replace the previous scan's.
 */
export function step(s: CaveState, input: Input): void {
  s.events.length = 0;
  if (s.status === 'won' || s.status === 'timeout') return;
  s.scan++;
  s.moved.fill(0);
  s.amoeba.growing = false;
  const { w, h, cells, stamp } = s;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (stamp[i] === s.scan) continue;
      const el = cells[i] ?? E.STEEL;
      if (el === E.SPACE || el === E.DIRT || el === E.STEEL || el === E.BRICK) continue;
      act(s, x, y, el, input);
    }
  }
  amoebaEndOfScan(s);
  clocks(s);
}
